import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import {
    BrushOptions,
    DeformOptions,
    LineDeformOptions,
    LineOptions,
    SmoothBrushOptions,
    Tools,
} from '~/state/atoms/toolsAtoms';
import {
    Measurement,
    MeasurementDraft,
    MeasurementKind,
} from '~/domain/measurement/measurement';
import { LineProfile, LineProfileDraft } from '~/domain/intensity/lineProfile';
import {
    IntensityRegion,
    IntensityRegionDraft,
} from '~/domain/intensity/intensityRegion';
import { IntensityShape } from '~/state/atoms/intensityToolAtoms';
import { VoxelSubRange } from '~/domain/services/imageService/gridGeometry';
import { PostProcessingMode } from '~/engine/util/algorithms/sdf/SDFPostProcessing';
import { DrawingConstraints } from './actions/DrawActionState';
import { Environment } from '../data/Environment';
import { ImageObject } from '../data/image/ImageObject';
import { RegionOfInterest } from '../data/structure/RegionOfInterest';
import type { EnvironmentViews } from '../view/EnvironmentViews';
import { PanZoom } from '../view/PanZoom';
import type { FrameRotationAccess, ToolConfig } from './ToolManager';
import { RegistrationMode } from './tools/RegistrationTool';
import type { CrossSectionToolCallbacks } from './tools/contouring-tools/cross-section/CrossSectionTool';
import { isGridRectangleSelectable } from './tools/contouring-tools/obliqueGating';

/**
 * How a surface takes the rectangle-select tool's output: the extrusion
 * depth the drag is resolved with, and the consumer of the resolved window.
 */
interface RectangleSelectAccess {
    /** One slice (2D) or the whole volume (3D). */
    mode: PostProcessingMode;
    /** Called with the dragged rectangle as a voxel window of the ROI's grid. */
    onSelected: (range: VoxelSubRange) => void;
}

/**
 * Sibling SUPPORT structures moved together with a SUPPORT transform, resolved
 * against the given environment's structure set. Undefined unless the option
 * is on and the target ROI is itself a SUPPORT structure. Shared by both
 * arming paths so the sibling rule cannot drift between them.
 */
export function resolveSiblingSupports(
    environment: Environment | undefined,
    structure: RegionOfInterest | undefined,
    transformAllSupport: boolean
): RegionOfInterest[] | undefined {
    if (!transformAllSupport || !structure) {
        return undefined;
    }
    const structureSet = environment?.structureSet;
    const domainRoi = structureSet?.parent.getDicomRoi(structure.roiId);
    if (
        !structureSet ||
        domainRoi?.interpretedType !== RoiInterpretedType.SUPPORT
    ) {
        return undefined;
    }
    return structureSet.structures.filter((sibling) => {
        if (sibling.roiId === structure.roiId) {
            return false;
        }
        const domainSibling = structureSet.parent.getDicomRoi(sibling.roiId);
        return domainSibling?.interpretedType === RoiInterpretedType.SUPPORT;
    });
}

/**
 * Fully-resolved primitives needed to assemble a {@link ToolConfig}.
 * Every field is already resolved by the caller (bulk/approval applied to
 * `structure`, sibling supports collected for `transform`) so this builder
 * itself resolves nothing and depends on no React or Recoil state.
 */
export interface ToolArmingInputs {
    tool: Tools;
    panZoom: PanZoom;
    environment: Environment | undefined;
    views: EnvironmentViews | undefined;
    structure: RegionOfInterest | undefined;
    image: ImageObject | undefined;
    siblingSupports: RegionOfInterest[] | undefined;
    brushOptions: BrushOptions;
    lineOptions: LineOptions;
    lineDeformOptions: LineDeformOptions;
    deformOptions: DeformOptions;
    smoothBrushOptions: SmoothBrushOptions;
    registrationMode: RegistrationMode | null;
    /**
     * Resolved drawing-constraint modifiers for brush/line. Required, not
     * optional: an optional field lets an arming path silently omit it and
     * draw unconstrained while the UI shows the constraint as active.
     * Pass `undefined` explicitly to arm without constraints.
     */
    constraints: DrawingConstraints | undefined;
    measurementMode: MeasurementKind;
    /** How the rotate tool reads and writes the frame of the pressed view. */
    frameRotation: FrameRotationAccess;
    /**
     * How the rectangle-select tool's drag is consumed. Required, not
     * optional, as `constraints` is: a surface with nothing to consume the
     * drag passes `undefined` explicitly, and the tool cannot be built for it.
     */
    rectangleSelect: RectangleSelectAccess | undefined;
    onMeasurementDraftChanged: (draft: MeasurementDraft | null) => void;
    onMeasurementCommit: (measurement: Measurement) => void;
    /** Which shape the intensity tool places on the next click. */
    intensityShape: IntensityShape;
    onLineProfileDraftChanged: (draft: LineProfileDraft | null) => void;
    onLineProfileCommit: (profile: LineProfile) => void;
    onIntensityRegionDraftChanged: (draft: IntensityRegionDraft | null) => void;
    onIntensityRegionCommit: (region: IntensityRegion) => void;
    onBrushSizeChanged: (diameter: number) => void;
    onSpreadChanged: (spreadPercent: number) => void;
    onDiameterChanged: (diameter: number) => void;
    onSmoothBrushDiameterChanged: (diameter: number) => void;
    /**
     * Receives whether the cross-section tool's contours can be undone or
     * redone, and whether any are drawn.
     */
    onCrossSectionsChanged: CrossSectionToolCallbacks['onCrossSectionsChanged'];
    /** Called when the geometry drawn with the cross-section tool changes. */
    onCrossSectionShapeChanged: CrossSectionToolCallbacks['onShapeChanged'];
}

/**
 * Assemble the per-tool {@link ToolConfig} for {@link ToolManager.setActiveTool}
 * from already-resolved primitives. Pure: no side effects, no state reads.
 */
export function buildToolConfig(inputs: ToolArmingInputs): ToolConfig {
    const base = {
        panZoom: inputs.panZoom,
        environment: inputs.environment,
        views: inputs.views,
    };

    switch (inputs.tool) {
        case 'brush':
            return {
                ...base,
                tool: 'brush',
                structure: inputs.structure,
                image: inputs.image,
                options: inputs.brushOptions,
                onBrushSizeChanged: inputs.onBrushSizeChanged,
                constraints: inputs.constraints,
            };
        case 'line':
            return {
                ...base,
                tool: 'line',
                structure: inputs.structure,
                image: inputs.image,
                options: inputs.lineOptions,
                constraints: inputs.constraints,
            };
        case 'registration':
            return {
                ...base,
                tool: 'registration',
                mode: inputs.registrationMode,
            };
        case 'boxZoom':
            return {
                ...base,
                tool: 'boxZoom',
            };
        case 'transform':
            return {
                ...base,
                tool: 'transform',
                structure: inputs.structure,
                siblingSupports: inputs.siblingSupports,
            };
        case 'lineDeform':
            return {
                ...base,
                tool: 'lineDeform',
                structure: inputs.structure,
                image: inputs.image,
                options: inputs.lineDeformOptions,
                onSpreadChanged: inputs.onSpreadChanged,
            };
        case 'deform':
            return {
                ...base,
                tool: 'deform',
                structure: inputs.structure,
                image: inputs.image,
                options: inputs.deformOptions,
                onDiameterChanged: inputs.onDiameterChanged,
            };
        case 'smoothBrush':
            return {
                ...base,
                tool: 'smoothBrush',
                structure: inputs.structure,
                options: inputs.smoothBrushOptions,
                onDiameterChanged: inputs.onSmoothBrushDiameterChanged,
            };
        // No constraints are armed on the tool: the drawing modifiers shape
        // the composed result, not the drawn contours.
        case 'cross-section':
            return {
                ...base,
                tool: 'cross-section',
                structure: inputs.structure,
                callbacks: {
                    onCrossSectionsChanged: inputs.onCrossSectionsChanged,
                    onShapeChanged: inputs.onCrossSectionShapeChanged,
                },
            };
        case 'rectangle-select': {
            if (!inputs.rectangleSelect) {
                throw new Error(
                    'The rectangle-select tool cannot arm: nothing on this surface consumes the dragged rectangle.'
                );
            }
            // Read at each drag: the texture grid moves under the armed tool.
            const { structure } = inputs;
            const getGrid = () => structure?.texture?.textureGrid;
            // The tool resolves the drag against the target ROI's own
            // texture grid, and reports a window indexing that grid, so the
            // drag and the clear cannot disagree about a series.
            //
            // A missing ROI/texture/grid means there is nothing to resolve
            // the drag against, and a grid that does not lie along the
            // patient axes means the drag cannot be reduced to a world box
            // without over-deleting (see isGridRectangleSelectable). Both
            // refuse the arm rather than fabricate geometry: the same
            // predicate the tool's own press gate reaches through
            // rectangleSelectAxis, so the two cannot disagree.
            const grid = getGrid();
            if (!grid || !isGridRectangleSelectable(grid)) {
                return { ...base, tool: 'pan' };
            }
            return {
                ...base,
                tool: 'rectangle-select',
                mode: inputs.rectangleSelect.mode,
                getGrid,
                onSelect: inputs.rectangleSelect.onSelected,
            };
        }
        case 'rotateView':
            return {
                ...base,
                tool: 'rotateView',
                getFrameRotation: inputs.frameRotation.getFrameRotation,
                onFrameRotationChanged:
                    inputs.frameRotation.onFrameRotationChanged,
            };
        case 'measurement':
            return {
                ...base,
                tool: 'measurement',
                mode: inputs.measurementMode,
                onDraftChanged: inputs.onMeasurementDraftChanged,
                onCommit: inputs.onMeasurementCommit,
            };
        // The intensity tool places a line or a region; the shape picks
        // which engine tool is built.
        case 'intensity':
            if (inputs.intensityShape !== 'line') {
                return {
                    ...base,
                    tool: 'intensityRegion',
                    shape: inputs.intensityShape,
                    onDraftChanged: inputs.onIntensityRegionDraftChanged,
                    onCommit: inputs.onIntensityRegionCommit,
                };
            }
            return {
                ...base,
                tool: 'lineProfile',
                onDraftChanged: inputs.onLineProfileDraftChanged,
                onCommit: inputs.onLineProfileCommit,
            };
        case 'poi-move':
        case 'pan':
            return {
                ...base,
                tool: 'pan',
            };
    }
    return assertNever(inputs.tool);
}

/**
 * Exhaustiveness guard: adding a member to {@link Tools} without an arm above
 * becomes a compile error here rather than a tool that silently arms pan.
 */
function assertNever(tool: never): never {
    throw new Error(`Unhandled tool: ${String(tool)}`);
}
